import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { forumAPI } from '../../../services/api';
import AdminReplyForm from './AdminReplyForm';
import './AdminForumDetails.css';

const AdminForumDetails = ({ forum, onClose, onUpdate }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showReplyForm, setShowReplyForm] = useState(false);

  const updateForumStatus = async (newStatus) => {
    try {
      setLoading(true);
      await forumAPI.updateForumStatus(forum.id, newStatus);
      onUpdate();
      onClose();
    } catch (err) {
      setError('Failed to update forum status');
      console.error('Error updating forum status:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsNotified = async () => {
    try {
      setLoading(true);
      await forumAPI.markForumAsNotified(forum.id);
      onUpdate();
      onClose();
    } catch (err) {
      setError('Failed to mark as notified');
      console.error('Error marking as notified:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { class: 'status-open', text: 'Open' },
      completed: { class: 'status-completed', text: 'Completed' },
      spam: { class: 'status-spam', text: 'Spam' },
      removed: { class: 'status-removed', text: 'Removed' }
    };
    
    const config = statusConfig[status] || { class: 'status-default', text: status };
    return <span className={`status-badge ${config.class}`}>{config.text}</span>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="admin-forum-details-overlay">
      <div className="admin-forum-details-modal">
        <div className="forum-details-header">
          <h2>Forum Question Details</h2>
          <button 
            className="close-btn"
            onClick={onClose}
            type="button"
          >
            ×
          </button>
        </div>

        <div className="forum-details-content">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="forum-details-info">
            <div className="forum-header-info">
              <h3 className="forum-title">
                {forum.title || 'No Title'}
              </h3>
              <div className="forum-status-info">
                {getStatusBadge(forum.status)}
                {!forum.notified_admin && (
                  <span className="unnotified-badge">New</span>
                )}
              </div>
            </div>

            <div className="forum-meta-info">
              <div className="meta-item">
                <strong>Asked by:</strong> {forum.user?.name || 'Unknown User'}
              </div>
              <div className="meta-item">
                <strong>Date:</strong> {formatDate(forum.created_at)}
              </div>
              <div className="meta-item">
                <strong>Status:</strong> {forum.status}
              </div>
              <div className="meta-item">
                <strong>Admin Notified:</strong> {forum.notified_admin ? 'Yes' : 'No'}
              </div>
            </div>

            <div className="forum-description">
              <h4>Question Description:</h4>
              <p>{forum.description}</p>
            </div>

            {forum.attachments && forum.attachments.length > 0 && (
              <div className="forum-attachments">
                <h4>Attachments ({forum.attachments.length}):</h4>
                <div className="attachment-list">
                  {forum.attachments.map((attachment, index) => {
                    const isImage = attachment.mime_type && attachment.mime_type.startsWith('image/');
                    return (
                      <div key={index} className="attachment-item">
                        {isImage ? (
                          <div className="image-attachment">
                            <img 
                              src={attachment.url} 
                              alt={`Attachment ${index + 1}`}
                              className="attachment-image"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                              }}
                            />
                            <a 
                              href={attachment.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="attachment-link"
                              style={{display: 'none'}}
                            >
                              View Image {index + 1}
                            </a>
                          </div>
                        ) : (
                          <a 
                            href={attachment.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="attachment-link"
                          >
                            {attachment.original_name || `Attachment ${index + 1}`}
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {forum.admin_replies && forum.admin_replies.length > 0 && (
              <div className="admin-replies">
                <h4>Admin Replies ({forum.admin_replies.length}):</h4>
                {forum.admin_replies.map((reply, index) => (
                  <div key={index} className="admin-reply">
                    <div className="reply-header">
                      <strong>Admin Reply</strong>
                      <span className="reply-date">
                        {formatDate(reply.created_at)}
                      </span>
                    </div>
                    <div className="reply-content">
                      <p>{reply.reply_text}</p>
                      {reply.attachments && reply.attachments.length > 0 && (
                        <div className="reply-attachments">
                          {reply.attachments.map((attachment, attIndex) => {
                            const isImage = attachment.mime_type && attachment.mime_type.startsWith('image/');
                            return (
                              <div key={attIndex} className="attachment-item">
                                {isImage ? (
                                  <div className="image-attachment">
                                    <img 
                                      src={attachment.url} 
                                      alt={`Admin Attachment ${attIndex + 1}`}
                                      className="attachment-image"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'block';
                                      }}
                                    />
                                    <a 
                                      href={attachment.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="attachment-link"
                                      style={{display: 'none'}}
                                    >
                                      View Admin Image {attIndex + 1}
                                    </a>
                                  </div>
                                ) : (
                                  <a 
                                    href={attachment.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="attachment-link"
                                  >
                                    {attachment.original_name || `Admin Attachment ${attIndex + 1}`}
                                  </a>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="forum-details-actions">
            <div className="status-actions">
              <h4>Update Status:</h4>
              <div className="status-buttons">
                <button
                  onClick={() => updateForumStatus('open')}
                  className={`btn btn-status ${forum.status === 'open' ? 'active' : ''}`}
                  disabled={loading}
                >
                  Open
                </button>
                <button
                  onClick={() => updateForumStatus('completed')}
                  className={`btn btn-status ${forum.status === 'completed' ? 'active' : ''}`}
                  disabled={loading}
                >
                  Completed
                </button>
                <button
                  onClick={() => updateForumStatus('spam')}
                  className={`btn btn-status ${forum.status === 'spam' ? 'active' : ''}`}
                  disabled={loading}
                >
                  Spam
                </button>
                <button
                  onClick={() => updateForumStatus('removed')}
                  className={`btn btn-status ${forum.status === 'removed' ? 'active' : ''}`}
                  disabled={loading}
                >
                  Removed
                </button>
              </div>
            </div>

            <div className="admin-actions">
              {!forum.notified_admin && (
                <button
                  onClick={markAsNotified}
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  Mark as Notified
                </button>
              )}
              <button
                onClick={() => setShowReplyForm(true)}
                className="btn btn-primary"
                disabled={loading}
              >
                Reply to Question
              </button>
            </div>
          </div>
        </div>
      </div>

      {showReplyForm && (
        <AdminReplyForm 
          forum={forum}
          onClose={() => setShowReplyForm(false)}
          onSuccess={() => {
            setShowReplyForm(false);
            onUpdate();
            onClose();
          }}
        />
      )}
    </div>
  );
};

export default AdminForumDetails;
